import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Modal, Form, Input, DatePicker, Select, InputNumber,
    Row, Col, Typography, message, Spin, Popconfirm, Tag, Tooltip, Space, Badge, Upload
} from 'antd';
import { PlusOutlined, FileTextOutlined, UploadOutlined, DeleteOutlined, EditOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { getLpos, createLpo, updateLpo, deleteLpo, getClients, downloadLpoStatement, imagepath } from '../../api';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

export default function LpoList() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
        fetchClients();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        const res = await getLpos();
        if (res.success) {
            setData(res.data);
        } else {
            message.error(res.message);
        }
        setLoading(false);
    };

    const fetchClients = async () => {
        const res = await getClients();
        if (res.success) {
            setClients(res.data);
        }
    };

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({ date: dayjs(), status: 'active', total_amount: 0 });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.resetFields();
        form.setFieldsValue({
            client_id: record.client_id,
            lpo_number: record.lpo_number,
            title: record.title,
            date: dayjs(record.date),
            total_amount: record.total_amount,
            status: record.status,
            remarks: record.remarks,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        const res = await deleteLpo(id);
        if (res.success) {
            message.success('LPO deleted');
            fetchData();
        } else {
            message.error(res.message);
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const formData = new FormData();

            Object.entries(values).forEach(([key, value]) => {
                if (key === 'date' && value) {
                    formData.append(key, value.format('YYYY-MM-DD'));
                } else if (key === 'attachment') {
                    if (value && value[0] && value[0].originFileObj) {
                        formData.append(key, value[0].originFileObj);
                    }
                } else if (value !== undefined && value !== null) {
                    formData.append(key, value);
                }
            });

            let res;
            if (editingId) {
                res = await updateLpo(editingId, formData);
            } else {
                res = await createLpo(formData);
            }

            if (res.success) {
                message.success(editingId ? 'LPO updated' : 'LPO created');
                setModalVisible(false);
                fetchData();
            } else {
                message.error(res.message);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDownloadStatement = async (id) => {
        try {
            message.loading({ content: 'Generating PDF...', key: 'download_lpo' });
            await downloadLpoStatement(id);
            message.success({ content: 'Statement downloaded', key: 'download_lpo' });
        } catch (err) {
            message.error({ content: 'Download failed', key: 'download_lpo' });
        }
    };

    const columns = [
        {
            title: 'LPO Number',
            dataIndex: 'lpo_number',
            key: 'lpo_number',
            render: (t, r) => (
                <Space>
                    <Text strong>{t}</Text>
                    {r.attachment && (
                        <Tooltip title="View LPO Document">
                            <a href={`${imagepath}${r.attachment}`} target="_blank" rel="noreferrer">
                                <FileTextOutlined style={{ color: '#1890ff' }} />
                            </a>
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: 'Client',
            dataIndex: ['client', 'name'],
            key: 'client',
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (d) => dayjs(d).format('DD-MM-YYYY'),
        },
        {
            title: 'Total Amount',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (a) => <Text strong>AED {Number(a).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>,
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            render: (b) => (
                <Badge
                    status={b > 0 ? 'success' : 'error'}
                    text={<Text strong style={{ color: b > 0 ? '#52c41a' : '#f5222d' }}>AED {Number(b).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>}
                />
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (s) => {
                let color = 'blue';
                if (s === 'closed') color = 'gray';
                if (s === 'void') color = 'red';
                return <Tag color={color}>{s.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Ledger">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/lpos/${record.id}`)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit LPO">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Download Statement">
                        <Button
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadStatement(record.id)}
                        />
                    </Tooltip>
                    <Popconfirm title="Delete LPO?" onConfirm={() => handleDelete(record.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2}>LPO Management</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Create New LPO
                </Button>
            </div>

            <Card bordered={false} className="shadow-sm">
                <Table
                    dataSource={data}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 15 }}
                />
            </Card>

            <Modal
                title={editingId ? "Edit LPO" : "Create New LPO"}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => setModalVisible(false)}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="client_id" label="Client" rules={[{ required: true }]}>
                                <Select placeholder="Select Client" showSearch optionFilterProp="children">
                                    {clients.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lpo_number" label="LPO Number" rules={[{ required: true }]}>
                                <Input placeholder="e.g. LPO-2024-001" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="title" label="Title/Project Name">
                        <Input placeholder="LPO Description or Title" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="total_amount" label="Total Amount (AED)" rules={[{ required: true }]}>
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                                <Select>
                                    <Option value="active">Active</Option>
                                    <Option value="closed">Closed</Option>
                                    <Option value="void">Void</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="attachment"
                                label="LPO Document"
                                valuePropName="fileList"
                                getValueFromEvent={e => Array.isArray(e) ? e : e && e.fileList}
                            >
                                <Upload beforeUpload={() => false} maxCount={1}>
                                    <Button icon={<UploadOutlined />}>Select File</Button>
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="remarks" label="Remarks">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
